import {
  Box,
  Typography,
  Container,
  Divider,
  IconButton,
  Tooltip,
  styled,
  useTheme,
  Chip
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import Logo from 'src/components/LogoSign';
import { useTranslation } from 'react-i18next';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';

const MainContent = styled(Box)(
  ({ theme }) => `
    height: 100%;
    display: flex;
    flex: 1;
    overflow: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: radial-gradient(ellipse at top, ${
      theme.palette.primary.main
    }08 0%, transparent 50%),
                radial-gradient(ellipse at bottom, ${
                  theme.palette.secondary?.main || theme.palette.primary.dark
                }08 0%, transparent 50%);
`
);

const GlassCard = styled(Box)(
  ({ theme }) => `
    background: ${
      theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 30, 0.8)'
        : 'rgba(255, 255, 255, 0.85)'
    };
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    padding: ${theme.spacing(7)};
    border-radius: ${theme.shape.borderRadius * 4}px;
    box-shadow: 0 12px 48px 0 rgba(31, 38, 135, 0.15);
    border: 1px solid ${
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(255, 255, 255, 0.6)'
    };
    text-align: center;
    max-width: 700px;
    width: 100%;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -50%;
      width: 100%;
      height: 100%;
      background: linear-gradient(to right, transparent, rgba(255,255,255,0.2), transparent);
      transform: skewX(-25deg);
      animation: shine 8s infinite;
    }

    @keyframes shine {
      0% { left: -50%; }
      15% { left: 150%; }
      100% { left: 150%; }
    }
`
);

const FloatingImageWrapper = styled(Box)(
  () => `
    position: relative;
    display: inline-block;
    animation: float 6s ease-in-out infinite;
    filter: drop-shadow(0 20px 30px rgba(0,0,0,0.12));
    margin-bottom: 32px;

    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-20px); }
      100% { transform: translateY(0px); }
    }
`
);

const PulseIcon = styled(BuildCircleOutlinedIcon)(
  ({ theme }) => `
    position: absolute;
    top: -10px;
    right: -10px;
    font-size: 48px;
    color: ${theme.palette.warning.main};
    animation: pulse 2s ease-in-out infinite;
    filter: drop-shadow(0 0 8px ${theme.palette.warning.main}40);

    @keyframes pulse {
      0%, 100% { 
        transform: scale(1);
        opacity: 1;
      }
      50% { 
        transform: scale(1.1);
        opacity: 0.8;
      }
    }
`
);

function StatusMaintenance() {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Status - Maintenance</title>
      </Helmet>
      <MainContent>
        <Container
          maxWidth="md"
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <GlassCard>
            <Box mb={4} display="flex" justifyContent="center">
              <Logo />
            </Box>

            <FloatingImageWrapper>
              <img
                alt="Maintenance"
                height={220}
                src="/static/images/status/maintenance.svg"
                style={{ display: 'block' }}
              />
              <PulseIcon />
            </FloatingImageWrapper>

            <Typography
              variant="h2"
              sx={{
                mb: 1.5,
                backgroundImage: `linear-gradient(135deg, ${
                  theme.palette.primary.main
                } 0%, ${
                  theme.palette.secondary?.main || theme.palette.primary.dark
                } 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                fontWeight: 800,
                letterSpacing: '-0.5px',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              {t("We'll Be Back Shortly")}
            </Typography>

            <Chip
              label={t('System Under Maintenance')}
              color="warning"
              size="small"
              sx={{
                mb: 3,
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 1
              }}
            />

            <Typography
              variant="h5"
              color="text.primary"
              fontWeight="500"
              sx={{
                mb: 2,
                lineHeight: 1.7,
                maxWidth: '85%',
                mx: 'auto',
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              {t(
                "We're currently performing scheduled maintenance to improve your experience."
              )}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 4,
                lineHeight: 1.8,
                maxWidth: '90%',
                mx: 'auto',
                fontSize: '1rem'
              }}
            >
              {t(
                'Please try again after some time. We apologize for any inconvenience and appreciate your patience.'
              )}
            </Typography>

            <Box
              sx={{
                mb: 4,
                p: 2.5,
                borderRadius: 2,
                background:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)',
                border: `1px solid ${
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.06)'
                }`,
                maxWidth: '80%',
                mx: 'auto'
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                <span role="img" aria-label="clock">
                  ⏱️
                </span>{' '}
                {t('Estimated Time')}:{' '}
                {localStorage.getItem('maintenanceModeDuration') ||
                  'indefinitely'}
              </Typography>
            </Box>

            <Divider
              sx={{
                my: 3,
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.08)',
                width: '60%',
                mx: 'auto'
              }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mb: 2,
                fontSize: '0.8rem'
              }}
            >
              {t('Stay connected with us')}
            </Typography>

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={1.5}
            >
              <Tooltip arrow placement="top" title="Facebook">
                <IconButton
                  color="primary"
                  sx={{
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.05)',
                      color: '#1877F2',
                      boxShadow: '0 8px 16px rgba(24, 119, 242, 0.3)'
                    }
                  }}
                >
                  <FacebookIcon fontSize="large" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow placement="top" title="Twitter">
                <IconButton
                  color="primary"
                  sx={{
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.05)',
                      color: '#1DA1F2',
                      boxShadow: '0 8px 16px rgba(29, 161, 242, 0.3)'
                    }
                  }}
                >
                  <TwitterIcon fontSize="large" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow placement="top" title="Instagram">
                <IconButton
                  color="primary"
                  sx={{
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.05)',
                      color: '#E4405F',
                      boxShadow: '0 8px 16px rgba(228, 64, 95, 0.3)'
                    }
                  }}
                >
                  <InstagramIcon fontSize="large" />
                </IconButton>
              </Tooltip>
            </Box>
          </GlassCard>
        </Container>
      </MainContent>
    </>
  );
}

export default StatusMaintenance;
